import classNames from 'classnames';
import Image from 'components/shared/image';
import { useAddItemsToCart, useUI } from 'hooks';
import { useEffect, useState } from 'react';
import SiteLink from 'components/shared/sitelink';
import { addLocaleToHref, reformatLocaleToContentfulLocale } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import ProgressBar from 'components/shared/progressBar';
import { formatPriceByStoreLocale } from 'lib/shopify';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import styles from './giftWithPurchasePopup.module.scss';
import ArrowRight from '../../shared/arrow-right';
import ArrowLeft from '../../shared/arrow-left';

const GiftWithPurchasePopup = ({ shopifyProducts, subTotalPrice, globalModules, locale }) => {
  const { productpage, giftwithpurchase } = globalModules || {};
  const addItems = useAddItemsToCart();

  const { closeCartPopup } = useUI();
  const router = useRouter();
  const [contentfulData, setContentfulData] = useState([]);

  useEffect(() => {
    if (!shopifyProducts || !shopifyProducts.length) return;
    const fetchProducts = async IDs => {
      const products = await fetch('/api/contentful/get-products-by-ids', {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          locale,
          productIds: IDs,
        }),
      });

      try {
        if (products.ok) {
          const productsWithContentfulData = await products.json();
          setContentfulData(productsWithContentfulData);
        }
        return null;
      } catch (e) {
        return products.status(500).json('There seems to have been an issue:', e);
      }
    };

    const IDs = shopifyProducts.map(item => item.freeProducts.map(({ id }) => id)).flat();

    if (IDs.length) {
      fetchProducts(IDs);
    }
  }, [shopifyProducts]);

  const handleAddToCart = async item => {
    addItems([
      {
        variantId: item.variantId,
        quantity: 1,
        customAttributes: [{ key: '_gift_with_purchase', value: 'true' }],
      },
    ]);
  };

  if (!shopifyProducts?.length) return null;

  const GwpItem = ({ item, discountMaxAmount, slidesTotal, currentSlideIndex, discountTitle }) => {
    const { title, featuredImage, handle } = item;

    const cData = contentfulData.find(({ slug }) => handle === slug);

    const isLocaleAvailable = cData?.availableLocales?.includes(
      reformatLocaleToContentfulLocale(locale)
    );

    return (
      <div className={classNames('keen-slider__slide', styles.itemSlide)}>
        <SiteLink
          locale={locale}
          href={addLocaleToHref(handle, locale, router)}
          onClick={closeCartPopup}
          className={classNames(styles.itemImage, { [styles.clickDisabled]: !isLocaleAvailable })}
          draggable={false}
        >
          <Image src={featuredImage} layout="fill" draggable={false} />
        </SiteLink>
        <div className={styles.contentContainer}>
          <div className={styles.titlesContainer}>
            {slidesTotal > 1 && (
              <span className={styles.currentNavInfo}>
                {currentSlideIndex + 1} {'/'} {slidesTotal}
              </span>
            )}
            <div className={styles.discountTitle}>{discountTitle}</div>
            <div className={styles.title}>{title}</div>
          </div>
          <div className={styles.progressBarAndAtc}>
            <div className={styles.progressBarWrapper}>
              <ProgressBar
                current={subTotalPrice}
                max={discountMaxAmount}
                color={giftwithpurchase?.progressBarColor?.text}
              />
            </div>

            {subTotalPrice >= discountMaxAmount ? (
              <button
                type="button"
                className={styles.addToCartButton}
                onClick={() => handleAddToCart(item)}
              >
                {productpage?.addToCart?.text}
              </button>
            ) : (
              <div className={styles.priceLeftToAdd}>
                {giftwithpurchase?.stillToAdd?.text}{' '}
                {formatPriceByStoreLocale({
                  locale,
                  price: Number(discountMaxAmount - subTotalPrice).toFixed(2),
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const GwpItemsCarousel = ({ slides }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [sliderRef, instanceRef] = useKeenSlider({
      initial: 0,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
    });

    return (
      <>
        <div className={styles.sliderContainer}>
          <div ref={sliderRef} className="keen-slider">
            {slides.map(itemGroup => {
              const { freeProducts, value: discountData, discountTitle } = itemGroup;
              const discountMaxAmount = parseInt(discountData.amount, 10);
              return (
                <GwpItem
                  discountMaxAmount={discountMaxAmount}
                  item={freeProducts[0]}
                  discountTitle={discountTitle}
                  key={itemGroup.discountId}
                  slidesTotal={slides.length}
                  currentSlideIndex={currentSlide}
                />
              );
            })}
          </div>
          {loaded && instanceRef.current && slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={e => e.stopPropagation() || instanceRef.current?.prev()}
                disabled={currentSlide === 0}
                className={styles.arrowLeft}
              >
                <ArrowLeft />
              </button>

              <button
                type="button"
                onClick={e => e.stopPropagation() || instanceRef.current?.next()}
                disabled={currentSlide === instanceRef.current.track.details.slides.length - 1}
                className={styles.arrowRight}
              >
                <ArrowRight />
              </button>
            </>
          )}
        </div>
        {/* START DOTS*/}
        {loaded && instanceRef.current && slides?.length > 1 && (
          <div className={styles.dots}>
            {[...Array(instanceRef.current.track.details.slides.length).keys()].map(idx => {
              return (
                <button
                  type="button"
                  tabIndex={0}
                  aria-label="Switch product picture"
                  className={classNames(
                    styles.productGallerySphere,
                    {
                      selectedSphere: currentSlide === idx,
                    },
                    currentSlide === idx ? styles.selectedSphere : ''
                  )}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx);
                  }}
                />
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={classNames(styles.container)}>
      <div className={styles.mainContent}>
        <GwpItemsCarousel slides={shopifyProducts} />
      </div>
    </div>
  );
};

export default GiftWithPurchasePopup;
